import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { API_URL } from "../config/api";
import PlaceInput from "../components/PlaceInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Loader2, Search, Ship, Anchor, ArrowRight, AlertCircle, Info } from "lucide-react";

const fmtMoney = (n) => (n == null ? "" : Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 }));
const money = (n, c) => `${fmtMoney(n)} ${c || ""}`.trim();
function totalsLine(tbc, primaryCur) {
  const entries = Object.entries(tbc || {}).filter(([, v]) => v);
  const extras = entries.filter(([c]) => c !== primaryCur);
  return extras.map(([c, v]) => `${fmtMoney(v)} ${c}`).join(" · ");
}

const EQUIP_OPTIONS = [
  { value: "20GP", label: "20' GP" },
  { value: "40GP", label: "40' GP" },
  { value: "40HC", label: "40' HC" },
  { value: "20RE", label: "20' Reefer" },
  { value: "40RH", label: "40' Reefer HC" },
];

const CARRIER_META = {
  cma: { name: "CMA CGM", color: "#0b3d6b" },
  hapag: { name: "Hapag-Lloyd", color: "#eb6209" },
};

export default function OceanRateCompare() {
  const token = localStorage.getItem("token");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    origin: "", destination: "", equipment: "40HC",
    quantity: 1, weightPerContainerKg: 18000, departureDate: "",
    doorMode: false, placeOfOrigin: "", placeOfDelivery: "",
  });
  const [originLabel, setOriginLabel] = useState("");
  const [destLabel, setDestLabel] = useState("");
  const [porLabel, setPorLabel] = useState("");
  const [podLabel, setPodLabel] = useState("");
  const [detail, setDetail] = useState(null);
  const [excluded, setExcluded] = useState({}); // label -> true when unticked
  const [savedUrn, setSavedUrn] = useState(null);
  const [savingUrn, setSavingUrn] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Reset charge selection each time a breakdown opens.
  useEffect(() => { setExcluded({}); setSavedUrn(null); }, [detail]);

  const chargeKey = (c, i) => `${c.category || "Other"}|${c.label}|${i}`;
  const selectedTotals = (() => {
    const out = {};
    (detail?.breakdown || []).forEach((c, i) => {
      if (excluded[chargeKey(c, i)] || c.included || !c.amount) return;
      const cur = c.currency || "USD";
      out[cur] = (out[cur] || 0) + Number(c.amount);
    });
    return out;
  })();

  const saveCapture = async () => {
    if (!detail) return;
    setSavingUrn(true);
    try {
      const excludedLabels = (detail.breakdown || [])
        .filter((c, i) => !c.included && excluded[chargeKey(c, i)])
        .map((c) => c.label);
      const { data } = await axios.post(`${API_URL}/ocean-rates/captures`, {
        carrier: detail.carrier, source: detail.source,
        origin: form.origin, destination: form.destination,
        pol: detail.pol, pod: detail.pod,
        equipment: form.equipment, quantity: Number(form.quantity) || 1,
        transit_days: detail.transit_days, service: detail.service,
        valid_until: detail.valid_until,
        breakdown: detail.breakdown || [],
        excluded_labels: excludedLabels,
        selected_totals: selectedTotals,
        totals_by_currency: detail.totals_by_currency || {},
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSavedUrn(data.urn);
      toast.success(`Rate captured — ${data.urn}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save capture");
    }
    setSavingUrn(false);
  };

  const copyUrn = () => {
    if (savedUrn) { navigator.clipboard.writeText(savedUrn); toast.success("URN copied"); }
  };

  useEffect(() => {
    axios.get(`${API_URL}/ocean-rates/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setStatus(data)).catch(() => setStatus(null));
    // eslint-disable-next-line
  }, []);

  const search = async () => {
    if (!form.origin || !form.destination) {
      toast.error("Enter origin and destination (UN/LOCODE)");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API_URL}/ocean-rates/compare`, {
        origin: form.origin.trim().toUpperCase(),
        destination: form.destination.trim().toUpperCase(),
        equipment: form.equipment,
        quantity: Number(form.quantity) || 1,
        weightPerContainerKg: Number(form.weightPerContainerKg) || 18000,
        departureDate: form.departureDate || null,
        placeOfOrigin: form.doorMode && form.placeOfOrigin ? form.placeOfOrigin.trim().toUpperCase() : null,
        placeOfDelivery: form.doorMode && form.placeOfDelivery ? form.placeOfDelivery.trim().toUpperCase() : null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setResult(data);
      if (data.available_offers) toast.success(`${data.available_offers} rate(s) · best ${data.best.total_amount} ${data.best.currency} (${data.best.carrier})`);
      else toast.warning("No live spot rates returned right now — see per-carrier status below");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Compare failed");
    } finally {
      setLoading(false);
    }
  };

  const bestPrice = result?.best?.total_amount;
  const bestIdx = result?.offers
    ? result.offers.findIndex((o) => o.available && o.total_amount && o.total_amount === bestPrice)
    : -1;

  return (
    <div className="space-y-4" data-testid="ocean-compare-panel">
      {/* Header / carrier chips */}
      <div className="rounded-xl border p-4 bg-gradient-to-r from-slate-50 to-blue-50">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Ship className="h-5 w-5 text-[#0b3d6b]" />
            <div>
              <div className="font-semibold text-slate-800">Compare Ocean Spot Rates</div>
              <div className="text-xs text-slate-500">One search — CMA CGM &amp; Hapag-Lloyd side by side, cheapest first</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {status?.carriers && Object.entries(status.carriers).map(([k, v]) => (
              <Badge key={k} variant={v.configured ? "default" : "outline"} className="text-xs"
                     style={v.configured ? { backgroundColor: CARRIER_META[k]?.color } : {}}
                     data-testid={`ocean-carrier-chip-${k}`}>
                {v.name}{v.configured ? "" : " · setup"}
              </Badge>
            ))}
          </div>
        </div>
      </div>

      {/* Single search form */}
      <div className="rounded-xl border p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">From — origin (port, city or door)</Label>
            <PlaceInput
              kind="all"
              allowFreeText={false}
              value={originLabel}
              placeholder="Search origin — e.g. Shanghai, Koper…"
              data-testid="ocean-origin"
              onSelect={(p) => { set("origin", p.code); setOriginLabel(p.label || `${p.name} (${p.code})`); }}
            />
          </div>
          <div>
            <Label className="text-xs">To — destination (port, city or door)</Label>
            <PlaceInput
              kind="all"
              allowFreeText={false}
              value={destLabel}
              placeholder="Search destination — e.g. Koper, Hamburg…"
              data-testid="ocean-destination"
              onSelect={(p) => { set("destination", p.code); setDestLabel(p.label || `${p.name} (${p.code})`); }}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs">Equipment</Label>
            <select data-testid="ocean-equipment" className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={form.equipment} onChange={(e) => set("equipment", e.target.value)}>
              {EQUIP_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Qty</Label>
            <Input data-testid="ocean-qty" type="number" min="1" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Weight/ctr (kg)</Label>
            <Input data-testid="ocean-weight" type="number" value={form.weightPerContainerKg} onChange={(e) => set("weightPerContainerKg", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Departure (≥4 days)</Label>
            <Input data-testid="ocean-date" type="date" value={form.departureDate} onChange={(e) => set("departureDate", e.target.value)} />
          </div>
        </div>
        <div className="flex items-center gap-2 pt-1">
          <input id="ocean-door-toggle" type="checkbox" checked={form.doorMode} data-testid="ocean-door-toggle"
            onChange={(e) => set("doorMode", e.target.checked)} className="h-4 w-4" />
          <Label htmlFor="ocean-door-toggle" className="text-sm cursor-pointer">
            Door-to-door (EXW / DAP) — add inland pre / on-carriage
          </Label>
        </div>
        {form.doorMode && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-lg border border-dashed border-blue-200 bg-blue-50/40 p-3">
            <div>
              <Label className="text-xs">Pre-carriage from — inland / door (origin)</Label>
              <PlaceInput
                kind="all" allowFreeText={false} value={porLabel}
                placeholder="Inland/city, e.g. Ljubljana, SI"
                data-testid="ocean-place-origin"
                onSelect={(p) => { set("placeOfOrigin", p.code); setPorLabel(p.label || `${p.name} (${p.code})`); }}
              />
              <p className="text-[11px] text-slate-400 mt-1">Ocean loads at the origin port above; truck picks up here.</p>
            </div>
            <div>
              <Label className="text-xs">On-carriage to — inland / door (destination)</Label>
              <PlaceInput
                kind="all" allowFreeText={false} value={podLabel}
                placeholder="Inland/city, e.g. Munich, DE"
                data-testid="ocean-place-delivery"
                onSelect={(p) => { set("placeOfDelivery", p.code); setPodLabel(p.label || `${p.name} (${p.code})`); }}
              />
              <p className="text-[11px] text-slate-400 mt-1">Ocean discharges at the destination port above; truck delivers here.</p>
            </div>
          </div>
        )}
        <Button onClick={search} disabled={loading} data-testid="ocean-compare-btn" className="bg-[#0b3d6b] hover:bg-[#0a3358]">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
          Compare rates
        </Button>
      </div>

      {/* Per-carrier status */}
      {result?.sources && (
        <div className="flex flex-wrap gap-3 text-xs" data-testid="ocean-sources">
          {Object.entries(result.sources).map(([k, v]) => (
            <span key={k} className={v.ok ? "text-emerald-600" : "text-amber-600"}>
              <b>{CARRIER_META[k]?.name || k}:</b> {v.ok ? `${v.available ?? v.count} offer(s)` : (
                v.error === "edge_rate_limit" ? "rate-limited, retry shortly"
                : v.error === "not_configured" ? "not configured"
                : (v.status === 504 ? "carrier timeout, retry" : (v.error || "unavailable")))}
            </span>
          ))}
        </div>
      )}

      {/* Merged results */}
      {result && (
        <div className="space-y-3" data-testid="ocean-results">
          {result.offers.length === 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5" />
              No live spot rates were returned for this lane/date right now. Carriers occasionally rate-limit or time out — check the status above and retry in a minute.
            </div>
          )}
          {result.offers.map((o, i) => {
            const meta = CARRIER_META[o.source] || {};
            const isBest = i === bestIdx;
            const extras = totalsLine(o.totals_by_currency, o.currency);
            return (
              <div key={`${o.source}-${o.offer_id || i}`}
                   className={`rounded-xl border p-4 flex items-center justify-between gap-4 cursor-pointer transition hover:shadow-md ${isBest ? "border-green-400 bg-green-50/60 ring-1 ring-green-300" : "border-slate-200"}`}
                   data-testid={`ocean-offer-${i}`}
                   onClick={() => o.available && setDetail(o)}>
                <div className="flex items-start gap-3 min-w-0">
                  <div className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: meta.color || "#334155" }}>
                    <Anchor className="h-4 w-4 text-white" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-800">{o.carrier}</span>
                      <Badge variant="outline" className="text-[10px] uppercase">Spot</Badge>
                      {o.multi_currency && <Badge variant="outline" className="text-[10px] uppercase border-amber-300 text-amber-600">Multi-currency</Badge>}
                      {isBest && <Badge className="bg-green-600 text-[10px] uppercase" data-testid={`ocean-best-${i}`}>★ Best price</Badge>}
                    </div>
                    <div className="flex items-center gap-1.5 text-sm text-slate-600 mt-1">
                      <span className="font-medium">{o.pol || form.origin}</span>
                      <ArrowRight className="h-3.5 w-3.5" />
                      <span className="font-medium">{o.pod || form.destination}</span>
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      {o.transit_days != null ? `${o.transit_days} days` : "—"}
                      {o.service ? ` · ${o.service}` : ""}
                      {o.valid_until ? ` · valid until ${o.valid_until}` : ""}
                    </div>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  {o.available && o.total_amount
                    ? <>
                        <div className={`text-lg font-bold ${isBest ? "text-green-700" : "text-slate-800"}`} data-testid={`ocean-price-${i}`}>{money(o.total_amount, o.currency)}</div>
                        {extras && <div className="text-[11px] text-amber-600 font-medium">+ {extras}</div>}
                        <div className="text-[11px] text-blue-600 flex items-center gap-1 justify-end mt-0.5" data-testid={`ocean-breakdown-link-${i}`}><Info className="h-3 w-3" /> View breakdown</div>
                      </>
                    : <span className="text-xs uppercase font-semibold text-amber-600">{o.error_reason || "Unavailable"}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Breakdown dialog — see exactly what each rate includes/excludes */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="ocean-breakdown-dialog">
          {detail && (() => {
            const tbc = Object.entries(detail.totals_by_currency || {}).filter(([, v]) => v);
            const cats = {};
            (detail.breakdown || []).forEach((c, i) => {
              const k = c.category || "Other";
              (cats[k] = cats[k] || []).push({ ...c, _i: i });
            });
            const selTotals = Object.entries(selectedTotals).filter(([, v]) => v);
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <span className="h-6 w-6 rounded flex items-center justify-center" style={{ backgroundColor: CARRIER_META[detail.source]?.color }}>
                      <Anchor className="h-3.5 w-3.5 text-white" />
                    </span>
                    {detail.carrier} — {detail.pol} → {detail.pod}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-3 text-sm">
                  <div className="rounded-lg bg-slate-50 border p-3">
                    <div className="text-xs text-slate-500">All-in total{tbc.length > 1 ? " (per currency — cannot be summed)" : ""}</div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                      {tbc.map(([c, v]) => (
                        <span key={c} className="font-bold text-slate-800" data-testid={`ocean-total-${c}`}>{money(v, c)}</span>
                      ))}
                    </div>
                    {detail.scope_note && <div className="text-[11px] text-slate-500 mt-2 flex items-start gap-1"><Info className="h-3 w-3 mt-0.5" />{detail.scope_note}</div>}
                  </div>
                  {Object.entries(cats).map(([cat, lines]) => {
                    const priced = lines.filter((c) => !c.included);
                    const groupKeys = priced.map((c) => chargeKey(c, c._i));
                    const allOn = groupKeys.length > 0 && groupKeys.every((k) => !excluded[k]);
                    const toggleGroup = () => setExcluded((prev) => {
                      const next = { ...prev };
                      groupKeys.forEach((k) => { if (allOn) next[k] = true; else delete next[k]; });
                      return next;
                    });
                    return (
                    <div key={cat}>
                      <div className="flex items-center gap-2 mb-1">
                        <input type="checkbox" checked={allOn} onChange={toggleGroup}
                          data-testid={`ocean-group-toggle-${cat}`} className="h-3.5 w-3.5 accent-[#0b3d6b]" />
                        <div className="text-xs font-semibold text-slate-500 uppercase">{cat}</div>
                      </div>
                      <div className="border rounded-lg divide-y">
                        {lines.map((c) => {
                          const k = chargeKey(c, c._i);
                          const on = !excluded[k];
                          if (c.included) {
                            return (
                              <div key={c._i} className="flex items-center gap-2 px-3 py-1.5 text-sm opacity-70">
                                <input type="checkbox" checked disabled className="h-3.5 w-3.5" title="Included in freight — no separate amount" />
                                <span className="text-slate-600 min-w-0 truncate flex-1">
                                  {c.label}
                                  {c.unit ? <span className="text-[10px] text-slate-400 ml-1">({c.unit})</span> : null}
                                </span>
                                <span className="shrink-0 text-emerald-600 text-xs">included</span>
                              </div>
                            );
                          }
                          return (
                          <label key={c._i} className={`flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer ${on ? "" : "opacity-40"}`}>
                            <input type="checkbox" checked={on}
                              onChange={() => setExcluded((prev) => { const n = { ...prev }; if (on) n[k] = true; else delete n[k]; return n; })}
                              data-testid={`ocean-charge-${c._i}`} className="h-3.5 w-3.5 accent-[#0b3d6b]" />
                            <span className="text-slate-700 min-w-0 truncate flex-1">
                              {c.label}
                              {c.unit ? <span className="text-[10px] text-slate-400 ml-1">({c.unit})</span> : null}
                            </span>
                            <span className="shrink-0 font-medium text-slate-800">
                              {money(c.amount, c.currency)}
                            </span>
                          </label>
                          );
                        })}
                      </div>
                    </div>
                    );
                  })}

                  <div className="rounded-lg border-2 border-[#0b3d6b]/20 bg-[#0b3d6b]/5 p-3" data-testid="ocean-selected-total">
                    <div className="text-xs font-semibold text-[#0b3d6b] uppercase mb-1">Selected charges — sum by currency</div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      {selTotals.length ? selTotals.map(([c, v]) => (
                        <span key={c} className="font-bold text-slate-800" data-testid={`ocean-selected-${c}`}>{money(v, c)}</span>
                      )) : <span className="text-sm text-slate-400">No charges selected</span>}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">Untick any charge or group (e.g. destination charges) to recalculate. Each currency is summed on its own — no FX conversion — so this may differ from the carrier's converted all-in above.</div>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap border-t pt-3">
                    {savedUrn ? (
                      <div className="flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 w-full">
                        <span className="text-xs text-slate-500">URN</span>
                        <span className="font-mono font-semibold text-emerald-800 text-sm" data-testid="ocean-urn">{savedUrn}</span>
                        <button onClick={copyUrn} data-testid="ocean-copy-urn" className="ml-auto text-xs px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700">Copy</button>
                      </div>
                    ) : (
                      <Button onClick={saveCapture} disabled={savingUrn} data-testid="ocean-save-capture" className="bg-[#0b3d6b] hover:bg-[#0a3358]">
                        {savingUrn ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                        Capture rate &amp; get URN
                      </Button>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-400">
                    Tip: compare like-for-like — both carriers include ocean freight + origin/destination terminal &amp; documentation surcharges. Captured rates are stored under a URN for later review, fallback and building an official offer.
                  </p>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
